import { Component } from 'react';
import type { ErrorInfo, ReactNode } from 'react';

/**
 * Catches render-time errors so a bug shows a message instead of a blank
 * page.
 *
 * This exists because of how the widget's field mismatch presented: a
 * component threw during render, React unmounted the entire tree, and the
 * player saw plain white — indistinguishable from a failed asset load,
 * and diagnosable only by opening a console the player does not have.
 * Whatever breaks next should say so on screen.
 */
interface Props {
  children: ReactNode;
}

interface State {
  message: string | null;
}

export default class ErrorBoundary extends Component<Props, State> {
  state: State = { message: null };

  static getDerivedStateFromError(err: unknown): State {
    return { message: err instanceof Error ? err.message : String(err) };
  }

  componentDidCatch(err: Error, info: ErrorInfo) {
    // The iframe has no other channel out; the host page's console is a
    // different origin. This is what an operator will be asked for.
    console.error('[checkout] render failed', err, info.componentStack);
  }

  render() {
    if (this.state.message === null) return this.props.children;
    return (
      <main className="app">
        <div className="card">
          <h2 className="heading">Something went wrong</h2>
          <p className="subheading">
            This checkout could not be displayed. Please close this window and
            try again.
          </p>
          {/* Shown, not hidden: a player who can quote this gives support
              something to act on. It is a message, never a token. */}
          <p className="error">{this.state.message}</p>
        </div>
      </main>
    );
  }
}
